import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Column, Table, TableProps } from '@spins/react-table';
import { EllipsisButton } from '@spins/react-ui-library';
import Formats from 'utils/formats';
import InfiniteScroll from 'react-infinite-scroll-component';
import LoadingSpinner from 'components/LoadingSpinner';
import usePrevious from 'hooks/use-previous';

interface StoreItemTableProps
  extends Pick<TableProps<any>, 'error' | 'rows' | 'totalRows' | 'showLoading' | 'onRowClick'> {
  type: string;
  onActionClick(key: string, id: string): void;
}

const StoreItemTable: React.FC<StoreItemTableProps> = ({
  type,
  error,
  rows,
  showLoading,
  totalRows,
  onRowClick,
  onActionClick
}) => {
  const [currentSortDirection, setCurrentSortDirection] = useState('DESC');
  const [currentSortedBy, setCurrentSortedBy] = useState('created');
  const [lastIndex, setLastIndex] = useState(15);
  const [loadMore, setLoadMore] = useState(0);
  const [groups, setGroups] = useState([]);

  const prevGroups: Array<any> = usePrevious<Array<any>>(rows);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getRows = () => {
    const end = loadMore * 15 + 15;
    if (end === 15) {
      setLastIndex(end);
      setGroups(
        rows
          .slice()
          .reverse()
          .slice(0, end)
      );
    } else {
      setTimeout(() => {
        setLastIndex(end);
        setGroups(
          rows
            .slice()
            .reverse()
            .slice(0, end)
        );
      }, 1000);
    }
    setLoadMore(prevState => prevState + 1);
  };

  useEffect(() => {
    if (loadMore === 0 && groups.length === 0 && rows.length > 0) {
      getRows();
    } else if (prevGroups && prevGroups.length !== rows.length) {
      setCurrentSortDirection('DESC');
      setCurrentSortedBy('created');
      setLastIndex(15);
      setLoadMore(0);
      setGroups(
        rows
          .slice()
          .reverse()
          .slice(0, 15)
      );
    } else if (prevGroups && prevGroups.length > 0 && !_.isEqual(prevGroups, rows)) {
      // After updating a group, we need to update this component state to show the changes
      setGroups(
        rows
          .slice()
          .reverse()
          .slice(0, lastIndex)
      );
    }
  }, [loadMore, rows.length, groups, getRows, lastIndex, prevGroups, rows]);

  /**
   * Will paginate and sort rows. Also, map store/item filters to be able to sort them
   * @param rows
   */
  const mapRows = (rows: Array<any>) => {
    // Map counts to header names to sort easily
    let result = rows.map(row => ({
      ...row,
      accounts: row.data.accounts !== null ? row.data.accounts.length : 0,
      stores: row.data.stores !== null ? row.data.stores.length : 0,
      items: row.data.items !== null ? row.data.items.length : 0,
      created: row.created.split('T')[0]
    }));

    // Sort
    if (currentSortedBy === 'name') {
      result = _.orderBy(
        result,
        [result => result.name.toLowerCase()],
        [currentSortDirection.toLowerCase()]
      );
    } else {
      result = _.orderBy(
        result,
        [currentSortedBy, result => result.name.toLowerCase()],
        [currentSortDirection.toLowerCase(), 'asc']
      );
    }

    return result;
  };

  const handleHeaderClick = (sortBy: string, sortDirection: string) => {
    setCurrentSortedBy(sortBy);
    setCurrentSortDirection(sortDirection);
  };

  const columns: Array<Column<any>> = [
    {
      accessor: 'name',
      header: 'Name',
      width: 60,
      isDraggable: false
    },
    ...(type === 'store'
      ? [
          {
            accessor: 'accounts',
            header: 'Accounts',
            width: 10,
            isDraggable: false
          },
          {
            accessor: 'stores',
            header: 'Stores',
            width: 10,
            isDraggable: false
          }
        ]
      : [
          {
            accessor: 'items',
            header: 'Items',
            width: 10,
            isDraggable: false
          }
        ]),
    {
      accessor: 'created',
      cell: value => Formats.date(value),
      header: 'Date Created',
      width: 15,
      isDraggable: false
    },
    {
      accessor: 'id',
      cell: value => (
        <div onClick={event => event.stopPropagation()}>
          <EllipsisButton
            overlayPlacement="left"
            options={[
              { label: 'View', onClick: () => onActionClick('View', value) },
              { label: 'Edit', onClick: () => onActionClick('Edit', value) },
              { label: 'Delete', onClick: () => onActionClick('Delete', value) }
            ]}
          />
        </div>
      ),
      header: '',
      width: 3.5,
      isDraggable: false
    }
  ];

  return (
    <InfiniteScroll
      dataLength={groups.length}
      next={getRows}
      hasMore={groups.length !== 0 && groups.length < rows.length}
      height={600}
      loader={
        <div className="retrieving-container">
          <LoadingSpinner />
        </div>
      }
      scrollThreshold={0.9}
    >
      <Table
        columns={columns}
        error={error}
        layoutFixed={true}
        queryState={{
          sorting: {
            sortedBy: currentSortedBy,
            sortDescending: currentSortDirection === 'DESC',
            sort: (attribute: string, descending: boolean) =>
              handleHeaderClick(attribute, descending ? 'DESC' : 'ASC')
          }
        }}
        showLoading={showLoading}
        rows={mapRows(groups)}
        totalRows={totalRows}
        onRowClick={onRowClick}
      />
    </InfiniteScroll>
  );
};

export default StoreItemTable;
